import { productStore } from './productStore';
import { asAmount, newLineId } from './budget';
import { dayBefore, isDay } from './productVersions';

/** Free dates and gaps are allowed; two covering one day are not, or today has two answers. */
export interface StatementOfWork {
  id: string;
  /** Inclusive, `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive, `YYYY-MM-DD`. */
  endDate: string;
  /** Euros, the ceiling for the whole span. */
  amount: number;
}

/** A SOW as the dialog holds it: the amount is still whatever was typed into the field. */
export interface StatementDraft {
  /** The one being edited, so it does not overlap itself. Absent while adding. */
  id?: string;
  startDate: string;
  endDate: string;
  amount: string;
}

/** `endDate` is the day its successor starts, which is what the API hands out. */
export interface SowSprint {
  id: string;
  number: number;
  /** `YYYY-MM-DD`. */
  startDate: string;
  endDate: string;
}

/** A sprint hands its boundary day to the next, so back-to-back picks would otherwise overlap. */
export const sprintLastDay = (sprints: SowSprint[], sprint: SowSprint): string => {
  const next = sprints
    .filter(each => each.startDate > sprint.startDate)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
  return next ? dayBefore(next.startDate) : sprint.endDate;
};

/** The span a pair of sprints covers: from the first day of one to the last day of the other. */
export const spanOfSprints = (
  sprints: SowSprint[],
  startId: string,
  endId: string,
): { startDate: string; endDate: string } => {
  const from = sprints.find(sprint => sprint.id === startId);
  const to = sprints.find(sprint => sprint.id === endId);
  return {
    startDate: from?.startDate ?? '',
    endDate: to ? sprintLastDay(sprints, to) : '',
  };
};

/** The nearest where none covers it: one saved before the dialog asked for sprints holds free dates. */
export const sprintOfDay = (sprints: SowSprint[], day: string): SowSprint | null => {
  const inOrderByStart = [...sprints].sort((a, b) => a.startDate.localeCompare(b.startDate));
  if (!inOrderByStart.length || !isDay(day)) return null;
  const covering = inOrderByStart.find(
    sprint => sprint.startDate <= day && day <= sprintLastDay(inOrderByStart, sprint),
  );
  if (covering) return covering;
  return day < inOrderByStart[0].startDate ? inOrderByStart[0] : inOrderByStart[inOrderByStart.length - 1];
};

/** In order whichever was clicked first: a range has no direction until it is read. */
export const sprintsBetween = (sprints: SowSprint[], oneId: string, otherId: string): SowSprint[] => {
  const inOrderByStart = [...sprints].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const first = inOrderByStart.findIndex(sprint => sprint.id === oneId);
  const second = inOrderByStart.findIndex(sprint => sprint.id === otherId);
  if (first === -1 || second === -1) return [];
  return inOrderByStart.slice(Math.min(first, second), Math.max(first, second) + 1);
};

/** The one being edited is excluded, or the dialog would refuse to reopen on what it holds. */
export const takenSprintIds = (sprints: SowSprint[], statements: StatementOfWork[], exceptId?: string): string[] => {
  const others = statements.filter(sow => sow.id !== exceptId);
  return sprints
    .filter(sprint => {
      const from = sprint.startDate;
      const to = sprintLastDay(sprints, sprint);
      return others.some(sow => sow.startDate <= to && from <= sow.endDate);
    })
    .map(sprint => sprint.id);
};

/** Refused as the range is drawn rather than after it is saved, the way a booked night is. */
export const rangeIsFree = (sprints: SowSprint[], taken: string[], oneId: string, otherId: string): boolean => {
  const span = sprintsBetween(sprints, oneId, otherId);
  return span.length > 0 && !span.some(sprint => taken.includes(sprint.id));
};

export const statementsStorageKey = (productId: string): string => `productSow:${productId}`;

/** The same id maker the bought-in cost lines use: these are mock-store rows in the same sense. */
export const newStatementId = newLineId;

const asStatement = (raw: unknown): StatementOfWork | null => {
  if (!raw || typeof raw !== 'object') return null;
  const { id, startDate, endDate, amount } = raw as Record<string, unknown>;
  if (typeof id !== 'string' || !id) return null;
  if (typeof startDate !== 'string' || !isDay(startDate)) return null;
  if (typeof endDate !== 'string' || !isDay(endDate) || endDate < startDate) return null;
  const ceiling = asAmount(amount);
  if (ceiling === null || ceiling === 0) return null;
  return { id, startDate, endDate, amount: ceiling };
};

/** Oldest first, which is how the card lists them and how an overlap is easiest to see. */
const inOrder = (statements: StatementOfWork[]): StatementOfWork[] =>
  [...statements].sort((a, b) => a.startDate.localeCompare(b.startDate));

// A value that does not parse reads as none.
export const readStatements = (productId: string): StatementOfWork[] => {
  try {
    const raw = productStore.getItem(statementsStorageKey(productId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    return inOrder(parsed.map(asStatement).filter((sow): sow is StatementOfWork => sow !== null));
  } catch {
    return [];
  }
};

export const writeStatements = (productId: string, statements: StatementOfWork[]): void => {
  productStore.setItem(statementsStorageKey(productId), JSON.stringify(inOrder(statements)));
};

/** The one already covering any part of this span, which is the reason a draft is refused. */
export const overlapping = (statements: StatementOfWork[], draft: StatementDraft): StatementOfWork | null => {
  if (!isDay(draft.startDate) || !isDay(draft.endDate)) return null;
  return (
    statements.find(sow => sow.id !== draft.id && sow.startDate <= draft.endDate && draft.startDate <= sow.endDate) ??
    null
  );
};

/** What is wrong with a draft, as a reason the dialog can name. Null when it can be saved. */
export type StatementProblem = 'noStart' | 'noEnd' | 'backwards' | 'noAmount' | 'overlaps';

export const statementProblem = (statements: StatementOfWork[], draft: StatementDraft): StatementProblem | null => {
  if (!isDay(draft.startDate)) return 'noStart';
  if (!isDay(draft.endDate)) return 'noEnd';
  if (draft.endDate < draft.startDate) return 'backwards';
  // A ceiling of nothing is not a ceiling, so zero is as much a gap as a blank field.
  const amount = asAmount(draft.amount);
  if (amount === null || amount === 0) return 'noAmount';
  if (overlapping(statements, draft)) return 'overlaps';
  return null;
};

/** Checked first, so a refused draft leaves the list alone rather than being saved half-read. */
export const withStatement = (statements: StatementOfWork[], draft: StatementDraft): StatementOfWork[] => {
  if (statementProblem(statements, draft)) return statements;
  const saved: StatementOfWork = {
    id: draft.id ?? newStatementId(),
    startDate: draft.startDate,
    endDate: draft.endDate,
    amount: asAmount(draft.amount) as number,
  };
  const kept = statements.filter(sow => sow.id !== saved.id);
  return inOrder([...kept, saved]);
};

export const withoutStatement = (statements: StatementOfWork[], id: string): StatementOfWork[] =>
  statements.filter(sow => sow.id !== id);
